import {
  useState,
  useMemo,
  createContext,
  useContext,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Spin, Result, Button, Image } from 'antd';
import styled from '@emotion/styled';
import { generateInvoiceImage } from 'services/api/conversation';
import type { GenerateInvoiceImageParams } from 'services/api/conversation';
import { useMemoizedFn, useMount } from 'ahooks';
import { useTranslate } from '@tolgee/react';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { useSendFileImage } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ImageUpload';
import { JustRightHeightModal } from 'components/styled/scoped';

export const StoreContext = createContext<{
  buyerNick: string;
  storeId: string;
  storeName: string | undefined;
}>({
  buyerNick: '',
  storeId: '',
  storeName: '',
});

const InvoiceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  max-height: 500px;
  & > div.ant-image {
    max-width: 100%;
    display: block;
    align-self: flex-start;
    & > img {
      height: 100%;
      object-fit: contain;
    }
  }
`;

const InvoiceView = forwardRef(
  (
    props: {
      data: GenerateInvoiceImageParams;
      setBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ) => {
    const { data, setBtnDisabled } = props;
    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState<string>('');
    const { t } = useTranslate();

    useImperativeHandle(ref, () => url);

    const fetchData = useMemoizedFn(async (data) => {
      try {
        setLoading(true);
        const res = await generateInvoiceImage(data);

        unstable_batchedUpdates(() => {
          setLoading(false);
          setError(false);
          setUrl(res);
          setBtnDisabled(false);
        });
      } catch (error) {
        unstable_batchedUpdates(() => {
          setLoading(false);
          setError(true);
          setBtnDisabled(true);
        });
      }
    });

    useMount(() => {
      fetchData(data);
    });

    if (loading) {
      return (
        <InvoiceContainer>
          <Spin tip={t('scene.chatWorkspace.orderTab.invoice.generating')} />
        </InvoiceContainer>
      );
    }

    if (error) {
      return (
        <InvoiceContainer>
          <Result
            status="error"
            title={t('scene.chatWorkspace.orderTab.invoice.error.generationFailed')}
            extra={[
              <Button type="primary" onClick={() => fetchData(data)}>
                {t('common.action.regenerate')}
              </Button>,
            ]}
          />
        </InvoiceContainer>
      );
    }

    return (
      <InvoiceContainer>
        <Image src={url} />
      </InvoiceContainer>
    );
  },
);

function useInvoice(trade: Conversation.Trade.TradeWithLogistics) {
  const [visible, setVisible] = useState(false);
  const { storeId } = useContext(StoreContext);
  const { t } = useTranslate();
  const invoiceInfo = useMemo(() => {
    return {
      externalTradeId: trade.externalTradeId,
      internalBuyerId: trade.internalBuyerId,
      internalStoreId: storeId,
    };
  }, [trade, storeId]);
  const sendFileImage = useSendFileImage();
  const ref = useRef<string>('');
  const sendInvoice = useMemoizedFn(async () => {
    if (!ref.current) return;
    sendFileImage(ref.current)
      .then(() => {
        setVisible(false);
      })
      .catch(() => {});
  });
  const [btnDisabled, setBtnDisabled] = useState(true);

  const modal = useMemo(() => {
    return (
      <JustRightHeightModal
        visible={visible}
        width={602}
        centered
        title={t('scene.chatWorkspace.orderTab.invoice.title.sendInvoice')}
        destroyOnClose
        onCancel={() => setVisible(false)}
        footer={[
          <Button onClick={() => setVisible(false)}>{t('common.action.cancel')}</Button>,
          <Button type="primary" disabled={btnDisabled} onClick={sendInvoice}>
            {t('common.action.send')}
          </Button>,
        ]}
      >
        <InvoiceView data={invoiceInfo} ref={ref} setBtnDisabled={setBtnDisabled} />
      </JustRightHeightModal>
    );
  }, [visible, invoiceInfo, sendInvoice, btnDisabled, t]);

  return {
    open: () => setVisible(!visible),
    invoiceModal: modal,
  };
}

export default useInvoice;
